import type { MatchModeId, PitchSkinId } from "@golazo/shared";

/**
 * La CARRERA: el modo offline de verdad.
 *
 * Eres un jugador, no un entrenador ni un presidente. Empiezas con 17 años en
 * el equipo de tu pueblo (el Ytuano FC), juegas temporadas cortas (solo ida),
 * y subes por la escalera de modos — 3v3 en el barrio, 5v5 en Regional, 7v7 en
 * Segunda y 11v11 en Primera — hasta que las piernas digan basta, hacia los 35.
 *
 * Todo este directorio es logica pura: nada de DOM, nada de `Date.now()` ni de
 * `Math.random`. Cada decision aleatoria sale de la semilla de la carrera, asi
 * que una carrera se puede reproducir entera en un test.
 */

export const CAREER_VERSION = 1;

export type Position = "DEL" | "MED" | "DEF";
export type Foot = "left" | "right";

/**
 * Como juega un club. Es su personalidad en el campo: se traduce a
 * instrucciones de equipo para la IA y a como hablan de el la prensa y tu
 * mister.
 */
export type ClubStyle = "cerrojo" | "presion" | "toque" | "patadon" | "contra";
export const CLUB_STYLES: readonly ClubStyle[] = ["cerrojo", "presion", "toque", "patadon", "contra"];

export interface Division {
  tier: number;
  name: string;
  modeId: MatchModeId;
}

/**
 * La escalera. Cada peldaño es un modo de juego distinto: subir de division
 * no es solo un rival mejor, es un deporte mas grande.
 */
export const DIVISIONS: readonly Division[] = [
  { tier: 0, name: "Liga del Barrio", modeId: "3v3" },
  { tier: 1, name: "Regional", modeId: "5v5" },
  { tier: 2, name: "Segunda", modeId: "7v7" },
  { tier: 3, name: "Primera", modeId: "11v11" },
];
export const TOP_TIER = DIVISIONS.length - 1;

export const CLUBS_PER_DIVISION = 8;
/** Solo ida: todos contra todos una vez. */
export const MATCHDAYS = CLUBS_PER_DIVISION - 1;
/** Suben los dos primeros y bajan los dos ultimos. */
export const MOVERS = 2;

export const START_AGE = 17;
/** A partir de aqui puedes colgar las botas al acabar la temporada. */
export const RETIRE_OPTIONAL_AGE = 33;
/** Y a esta edad ya no hay contrato que te salve. */
export const RETIRE_FORCED_AGE = 35;

/**
 * Los cuatro atributos. Son los que el partido puede NOTAR (ver `talent.ts`):
 * velocidad, potencia de disparo, pase (precision cruzada a la carrera) y
 * fondo (cuanto tardas en cansarte). Escala de 30 a 99, como una ficha.
 */
export interface Attributes {
  pace: number;
  shooting: number;
  passing: number;
  stamina: number;
}
export type AttributeKey = keyof Attributes;
export const ATTRIBUTE_KEYS: readonly AttributeKey[] = ["pace", "shooting", "passing", "stamina"];
export const ATTR_MIN = 30;
export const ATTR_MAX = 99;

export interface Club {
  id: string;
  name: string;
  tier: number;
  /** Fuerza, en la misma escala que los atributos (30-95). */
  rating: number;
  style: ClubStyle;
  kitId: string;
  /** Apellido del entrenador. */
  coach: string;
  /** Su jugador franquicia: la prensa siempre habla de el. */
  star: string;
  /** Su fama, en una frase (clave de `t()`): cada rival tiene su chiste. */
  quirk: string;
  stadium: Stadium;
}

/**
 * Su campo. Solo aspecto: el cesped (una piel de `PITCH_SKINS`), el nombre, y
 * cuanta gente va (0-1). En el barrio van cuatro padres y un perro; en
 * Primera no cabe un alfiler.
 */
export interface Stadium {
  name: string;
  skinId: PitchSkinId;
  crowd: number;
}

export interface CareerPlayer {
  name: string;
  number: number;
  position: Position;
  foot: Foot;
  age: number;
  attributes: Attributes;
  /** Oculto: cuanto crece este jugador (0,85-1,15). No se enseña nunca. */
  potential: number;
}

/** Un partido de liga ya jugado, en formato compacto: indices en `members`. */
export type CompactResult = [home: number, away: number, homeGoals: number, awayGoals: number];

/** Tu linea en un partido. */
export interface MatchLine {
  matchday: number;
  opponent: string;
  home: boolean;
  goalsFor: number;
  goalsAgainst: number;
  goals: number;
  assists: number;
  mvp: boolean;
  /** Nota del partido, 3,0-10,0. */
  grade: number;
  /** No llegaste a jugarlo (cerraste el juego a mitad). */
  absent?: boolean;
}

export interface SeasonState {
  /** 1, 2, 3… */
  number: number;
  /** Los clubes de cada division ESTA temporada (ids), en orden de sorteo. */
  members: string[][];
  /** Resultados por division, por jornada. */
  results: CompactResult[][][];
  /** Proxima jornada a jugar (0..MATCHDAYS). MATCHDAYS = temporada acabada. */
  matchday: number;
  /** Tu division y tu club esta temporada. */
  tier: number;
  club: string;
  lines: MatchLine[];
}

export interface SeasonSummary {
  number: number;
  age: number;
  tier: number;
  club: string;
  clubName: string;
  position: number;
  points: number;
  played: number;
  goals: number;
  assists: number;
  mvps: number;
  /** Media de notas. */
  grade: number;
  champion: boolean;
  promoted: boolean;
  relegated: boolean;
}

export interface Offer {
  club: string;
  /** Division en la que jugara ese club la temporada que viene. */
  tier: number;
  /** Lo que dice el agente para vendertela. */
  pitch: number;
}

/**
 * Quien te escribe. Cada uno es una conversacion del movil: el mister, tu
 * agente, tu madre, el grupo del vestuario, los rivales que te pican, el
 * periodico (que se lee como noticias) y la grada (como una red social).
 */
export type Sender = "coach" | "agent" | "press" | "fans" | "mom" | "system" | "squad" | "rival";

/**
 * Un mensaje del movil. Se guarda la CLAVE y sus parametros, no el texto: si
 * cambias de idioma, el movil entero cambia con el.
 */
export interface PhoneMessage {
  id: number;
  season: number;
  matchday: number;
  from: Sender;
  key: string;
  params?: Record<string, string | number>;
  /** Quien lo dice, cuando no basta con `from`: el compañero del grupo, la estrella rival. */
  who?: string;
  /** Lo has escrito tu (una respuesta): va a la derecha, como en cualquier chat. */
  mine?: boolean;
  /** Espera respuesta: id de `QUESTIONS` (ver `phoneTalk.ts`). */
  ask?: string;
  /** La respuesta elegida (indice de la opcion). Sin ella, aun se puede contestar. */
  answer?: number;
  /** Lleva adjunto: el informe del rival o el encargo del mister. */
  card?: "scout" | "objective";
}

/**
 * Como te llevas con la gente, de 0 a 100 (50 de salida). Cambia con lo que
 * contestas en el movil y con lo que haces en el campo, y cada una se nota en
 * el juego (ver `phoneTalk.ts`): el mister te hace crecer mas o menos, el
 * vestuario juega mejor o peor contigo y la aficion trae mejores ofertas.
 */
export interface Relations {
  coach: number;
  squad: number;
  fans: number;
}
export type RelationKey = keyof Relations;

/** Lo que te pide el mister para un partido. `target` segun la clase (ver `phoneTalk.ts`). */
export type ObjectiveKind = "score" | "shots" | "assist" | "passes" | "recoveries" | "cleanSheet" | "win";

export interface Objective {
  season: number;
  matchday: number;
  kind: ObjectiveKind;
  target: number;
  /** Como acabo. Sin esto, esta por jugar. */
  result?: "done" | "failed";
}

export interface Title {
  season: number;
  tier: number;
  club: string;
  clubName: string;
}

export interface CareerTotals {
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  goals: number;
  assists: number;
  mvps: number;
}

export type CareerPhase = "season" | "offseason" | "retired";

export interface Career {
  version: number;
  seed: number;
  player: CareerPlayer;
  clubs: Club[];
  season: SeasonState;
  phase: CareerPhase;
  /** Ofertas abiertas (solo en `offseason`). */
  offers: Offer[];
  /** Hacia donde va tu club la temporada que viene (en `offseason`). */
  nextTier: number;
  history: SeasonSummary[];
  titles: Title[];
  totals: CareerTotals;
  /** El movil, del mas viejo al mas nuevo. Se recorta a `FEED_LIMIT`. */
  feed: PhoneMessage[];
  nextMessageId: number;
  /** Clubes con los que te has cambiado la camiseta. */
  swaps: string[];
  /**
   * Camisetas ganadas en ESTA carrera (por temporada, cambio o premio). Las
   * desbloqueadas de verdad viven aparte (`unlocks.ts`): una carrera nueva no
   * te quita lo que ganaste en la anterior.
   */
  earnedKits: string[];
  /** Mejor media alcanzada. */
  peakOverall: number;
  /** Division mas alta en la que has jugado. */
  peakTier: number;
  /**
   * Partido empezado y no apuntado. Si al volver sigue aqui es que se cerro
   * el juego a mitad: el partido se da por jugado sin ti. El resultado es el
   * resultado.
   */
  pending: { season: number; matchday: number } | null;
  /*
   * Lo del movil que tiene consecuencias. Opcional: las carreras guardadas
   * antes de que existiera siguen cargando (y empiezan en 50).
   */
  relations?: Relations;
  /** El encargo del mister para el proximo partido (y como acabo el ultimo). */
  objective?: Objective | null;
  /** Encargos cumplidos y encargados en toda la carrera. */
  objectives?: { done: number; set: number };
  /** Has picado al rival de esta jornada: sale mas motivado (y se nota el resultado). */
  provoked?: { season: number; matchday: number } | null;
}

export const FEED_LIMIT = 80;
